import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Popper,
  ClickAwayListener,
  MenuList,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  AccountCircle,
  AttachFile as AttachFileIcon,
  PushPin as PinIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  Reply as ReplyIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { messageService, attachmentService, aiService } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import MessageAIFeatures from './MessageAIFeatures';

const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '👏'];

const ProjectChat = ({ projectId, currentUserId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState({ users: [], tasks: [] });
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [threadReplies, setThreadReplies] = useState({});

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const handleSuggestedReplyClick = (reply) => {
    setNewMessage(reply);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const response = await messageService.getByProject(projectId);
      setMessages(response.data.data);
      if (loading) setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [projectId]);

  useEffect(() => {
    if (!loading) scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    if (messages.length > 0) {
      const unreadMessages = messages.filter(m => 
        m.user_id !== currentUserId && // Not my messages
        (!m.reads || !m.reads.some(r => r.user_id === currentUserId)) // Not read by me
      );
      
      if (unreadMessages.length > 0) {
        unreadMessages.forEach(m => {
          messageService.markAsRead(m.id).catch(err => console.error('Error marking as read:', err));
        });
      }
    }
  }, [messages, currentUserId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && pendingAttachments.length === 0) || sending) return;

    // Détecter si c'est une commande pour ERA
    const isEraCommand = newMessage.trim().startsWith('@ERA');
    
    setSending(true);
    if (isEraCommand) setAiProcessing(true);

    try {
      const attachmentIds = pendingAttachments.map(a => a.id);

      if (isEraCommand) {
        // Traiter via le service IA
        const command = newMessage.trim().substring(4).trim(); // Enlever "@ERA"
        const response = await aiService.executeCommand({
          command,
          projectId
        });
        
        // Ajouter un message système ou de l'IA pour confirmer l'action
        if (response.data.success) {
          // On peut éventuellement envoyer un message automatique pour dire que l'IA a agi
          await messageService.createProjectMessage(projectId, {
            content: `🤖 **ERA Assistant** : ${response.data.message}`,
          });
        }
      } else if (replyingTo) {
        // Envoyer une réponse
        await messageService.replyToMessage(replyingTo.id, {
          content: newMessage,
          attachment_ids: attachmentIds
        });
        setReplyingTo(null);
      } else {
        // Envoyer un message normal
        await messageService.createProjectMessage(projectId, {
          content: newMessage,
          attachment_ids: attachmentIds
        });
      }

      setNewMessage('');
      setPendingAttachments([]);
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      // Afficher l'erreur si c'est une commande IA
      if (isEraCommand) {
        const errorMessage = error.response?.data?.error || "Erreur lors de l'exécution de la commande IA";
        alert(errorMessage);
      }
    } finally {
      setSending(false);
      setAiProcessing(false);
    }
  };

  const handleInputChange = async (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewMessage(value);
    setCursorPosition(cursorPos);

    // Détecter les mentions @ ou #
    const textBeforeCursor = value.substring(0, cursorPos);
    
    // Si on commence à taper @ERA, on ne montre pas les suggestions de membres habituelles
    if (textBeforeCursor.match(/@ERA$/i)) {
      setShowMentions(false);
      return;
    }

    // Support basic alphanumeric + French accents for both users and tasks
    const mentionMatch = textBeforeCursor.match(/@([\w\u00C0-\u00FF]*)$/) || textBeforeCursor.match(/#([\w\u00C0-\u00FF]*)$/);

    if (mentionMatch && projectId) {
      const query = mentionMatch[1];
      setMentionQuery(query);
      setShowMentions(true);

      try {
        const response = await messageService.getMentionSuggestions(projectId, query);
        setMentionSuggestions(response.data.data);
      } catch (error) {
        console.error('Error fetching mention suggestions:', error);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (mention, type) => {
    const textBeforeCursor = newMessage.substring(0, cursorPosition);
    const textAfterCursor = newMessage.substring(cursorPosition);

    // Trouver le début de la mention
    const mentionStartMatch = textBeforeCursor.match(/@[\w\u00C0-\u00FF]*$|#[\w\u00C0-\u00FF]*$/);
    if (!mentionStartMatch) return;

    const mentionStart = textBeforeCursor.lastIndexOf(mentionStartMatch[0]);
    const beforeMention = newMessage.substring(0, mentionStart);

    const mentionText = type === 'user' ? `@${mention.name}` : `#${mention.id}`;
    const newText = beforeMention + mentionText + ' ' + textAfterCursor;

    setNewMessage(newText);
    setShowMentions(false);

    // Remettre le focus sur le champ de texte
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);
  };

  const loadThreadReplies = async (messageId) => {
    try {
      const response = await messageService.getThreadReplies(messageId);
      setThreadReplies(prev => ({
        ...prev,
        [messageId]: response.data.data
      }));
    } catch (error) {
      console.error('Error loading thread replies:', error);
    }
  };

  const toggleThread = async (messageId) => {
    const isExpanded = expandedThreads[messageId];
    setExpandedThreads(prev => ({
      ...prev,
      [messageId]: !isExpanded
    }));

    if (!isExpanded && !threadReplies[messageId]) {
      await loadThreadReplies(messageId);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('related_type', 'project');
        formData.append('related_id', projectId);

        const response = await attachmentService.upload(formData);
        setPendingAttachments(prev => [...prev, response.data.data]);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Erreur lors du téléchargement du fichier');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (attachmentId) => {
    setPendingAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleMessageAction = (event, message) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handlePinMessage = async () => {
    if (!selectedMessage) return;
    try {
      await messageService.togglePin(selectedMessage.id);
      fetchMessages();
    } catch (error) {
      console.error('Error pinning message:', error);
    }
    handleCloseMenu();
  };

  const handleReaction = async (emoji) => {
    if (!selectedMessage) return;
    try {
      const existingReaction = selectedMessage.reactions?.find(
        r => r.user_id === currentUserId && r.emoji === emoji
      );

      if (existingReaction) {
        await messageService.removeReaction(selectedMessage.id, emoji);
      } else {
        await messageService.addReaction(selectedMessage.id, emoji);
      }
      fetchMessages();
    } catch (error) {
      console.error('Error handling reaction:', error);
    }
    handleCloseMenu();
  };

  const isCurrentUser = (userId) => userId === currentUserId;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            Discussion de Projet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Espace d'échange pour toute l'équipe
          </Typography>
        </Box>
        {messages.filter(m => m.is_pinned).length > 0 && (
          <Chip 
            icon={<PinIcon sx={{ fontSize: 16 }} />} 
            label={`${messages.filter(m => m.is_pinned).length} épinglé(s)`} 
            size="small" 
            color="warning" 
            variant="outlined" 
          />
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: 'background.default', minHeight: '300px', maxHeight: '400px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
            <Typography variant="body2">Aucun message pour le moment.</Typography>
            <Typography variant="caption">Commencez la discussion !</Typography>
          </Box>
        ) : (
          <List>
            {messages.map((msg) => {
              const isMe = isCurrentUser(msg.user_id);
              const parentMessage = msg.parent_id ? messages.find(m => m.id === msg.parent_id) : null;

              return (
                <React.Fragment key={msg.id}>
                <ListItem
                  id={`message-${msg.id}`}
                  alignItems="flex-start"
                  sx={{
                    flexDirection: isMe ? 'row-reverse' : 'row',
                    mb: 1
                  }}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={(e) => handleMessageAction(e, msg)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemAvatar sx={{ minWidth: 40, [isMe ? 'ml' : 'mr']: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: isMe ? 'primary.main' : 'secondary.main' }}>
                      {msg.user_name ? msg.user_name.charAt(0).toUpperCase() : <AccountCircle />}
                    </Avatar>
                  </ListItemAvatar>
                  <Box
                    sx={{
                      maxWidth: '70%',
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: msg.is_pinned ? 'warning.light' : (isMe ? 'primary.light' : 'background.paper'),
                      color: msg.is_pinned ? 'warning.contrastText' : (isMe ? 'primary.contrastText' : 'text.primary'),
                      boxShadow: 1,
                      position: 'relative'
                    }}
                  >
                    {parentMessage && (
                      <Box 
                        sx={{ 
                          mb: 1, 
                          pl: 1, 
                          borderLeft: '2px solid', 
                          borderColor: 'text.disabled',
                          opacity: 0.7,
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          const el = document.getElementById(`message-${msg.parent_id}`);
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          Réponse à {parentMessage.user_name}
                        </Typography>
                        <Typography variant="caption" display="block" noWrap>
                          {parentMessage.content || 'Pièce jointe'}
                        </Typography>
                      </Box>
                    )}

                    {msg.is_pinned && (
                      <PinIcon 
                        sx={{ 
                          position: 'absolute', 
                          top: -10, 
                          right: -10, 
                          fontSize: 16, 
                          color: 'warning.main',
                          bgcolor: 'background.paper',
                          borderRadius: '50%',
                          p: 0.2
                        }} 
                      />
                    )}
                    <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', mb: 0.5 }}>
                      {msg.user_name}
                    </Typography>
                    {msg.content_html ? (
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          '& .mention-user': {
                            color: '#0079BF',
                            backgroundColor: 'rgba(0, 121, 191, 0.1)',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontWeight: 600,
                            cursor: 'pointer'
                          },
                          '& .mention-task': {
                            color: '#5AAC44',
                            backgroundColor: 'rgba(90, 172, 68, 0.1)',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: msg.content_html }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </Typography>
                    )}
                    
                    {/* Attachments */}
                    {msg.attachments && msg.attachments.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {msg.attachments.map(att => (
                          <Chip
                            key={att.id}
                            label={att.original_name}
                            size="small"
                            icon={<AttachFileIcon />}
                            component="a"
                            href={`${attachmentService.getDownloadUrl(att.id)}?token=${localStorage.getItem('era_token')}`}
                            target="_blank"
                            clickable
                            sx={{ bgcolor: 'rgba(0,0,0,0.1)' }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Reactions */}
                    {msg.reactions && msg.reactions.length > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {Object.entries(
                          msg.reactions.reduce((acc, r) => {
                            acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                            return acc;
                          }, {})
                        ).map(([emoji, count]) => (
                          <Chip
                            key={emoji}
                            label={`${emoji} ${count}`}
                            size="small"
                            sx={{ height: 20, fontSize: '0.75rem', px: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}

                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, textAlign: 'right', fontSize: '0.7rem' }}>
                      {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true, locale: fr })}
                    </Typography>

                    {/* Read receipts for my messages */}
                    {isMe && msg.reads && msg.reads.length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Tooltip title={`Vu par : ${msg.reads.map(r => r.user_name).join(', ')}`} arrow>
                          <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>
                            <VisibilityIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              {msg.reads.length}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </ListItem>

                {/* Thread replies */}
                {!msg.parent_id && msg.reply_count > 0 && (
                  <Box sx={{ ml: isMe ? 0 : 6, mr: isMe ? 6 : 0, mb: 1 }}>
                    <Chip
                      label={expandedThreads[msg.id] ? `Masquer les ${msg.reply_count} réponse(s)` : `Voir les ${msg.reply_count} réponse(s)`}
                      size="small"
                      onClick={() => toggleThread(msg.id)}
                      sx={{ fontSize: '0.7rem', height: 22, cursor: 'pointer' }}
                      color="primary"
                      variant="outlined"
                    />
                    {expandedThreads[msg.id] && threadReplies[msg.id] && (
                      <Box sx={{ mt: 1, ml: 2, pl: 2, borderLeft: '3px solid #e0e0e0' }}>
                        {threadReplies[msg.id].map(reply => {
                          const isReplyMe = isCurrentUser(reply.user_id);
                          return (
                            <Box key={reply.id} sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, bgcolor: isReplyMe ? 'primary.main' : 'secondary.main', fontSize: '0.7rem' }}>
                                {reply.user_name ? reply.user_name.charAt(0).toUpperCase() : '?'}
                              </Avatar>
                              <Box sx={{ flex: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                                  {reply.user_name}
                                </Typography>
                                {reply.content_html ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.8rem',
                                      whiteSpace: 'pre-wrap',
                                      '& .mention-user': { color: '#0079BF', backgroundColor: 'rgba(0, 121, 191, 0.1)', padding: '2px 6px', borderRadius: '3px', fontWeight: 600 },
                                      '& .mention-task': { color: '#5AAC44', backgroundColor: 'rgba(90, 172, 68, 0.1)', padding: '2px 6px', borderRadius: '3px', fontWeight: 600 }
                                    }}
                                    dangerouslySetInnerHTML={{ __html: reply.content_html }}
                                  />
                                ) : (
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                                    {reply.content}
                                  </Typography>
                                )}
                                {reply.reactions && reply.reactions.length > 0 && (
                                  <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                                    {Object.entries(reply.reactions.reduce((acc, r) => { acc[r.emoji] = (acc[r.emoji] || 0) + 1; return acc; }, {})).map(([emoji, count]) => (
                                      <Chip key={emoji} label={`${emoji} ${count}`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                    ))}
                                  </Box>
                                )}
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.3, opacity: 0.6, fontSize: '0.65rem' }}>
                                  {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true, locale: fr })}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </List>
        )}
      </Box>

      {/* Input Area */}
      <Box component="form" onSubmit={handleSend} sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        {/* AI Features */}
        <MessageAIFeatures
          messages={messages}
          selectedMessage={selectedMessage}
          onSuggestedReplyClick={handleSuggestedReplyClick}
        />

        {pendingAttachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {pendingAttachments.map(att => (
              <Chip
                key={att.id}
                label={att.original_name}
                onDelete={() => handleRemoveAttachment(att.id)}
                size="small"
                icon={<AttachFileIcon />}
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton color="primary" component="label" disabled={isUploading}>
            <input 
              type="file" 
              hidden 
              multiple 
              onChange={handleFileSelect} 
              ref={fileInputRef}
            />
            {isUploading ? <CircularProgress size={24} /> : <AttachFileIcon />}
          </IconButton>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            variant="outlined"
            placeholder={aiProcessing ? "ERA réfléchit..." : "Tapez votre message ou @ERA pour une commande..."}
            value={newMessage}
            onChange={handleInputChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                handleSend(e);
              }
            }}
            disabled={sending || aiProcessing}
            inputRef={inputRef}
            InputProps={{
              startAdornment: aiProcessing && (
                <CircularProgress size={20} sx={{ mr: 1 }} />
              )
            }}
            sx={{ 
              bgcolor: 'background.paper',
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
          />
          <IconButton type="submit" color="primary" disabled={sending || (!newMessage.trim() && pendingAttachments.length === 0)}>
            {sending ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Mention Autocomplete Popup */}
      <Popper
        open={showMentions}
        anchorEl={inputRef.current}
        placement="top-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setShowMentions(false)}>
          <Paper sx={{ maxHeight: 200, overflow: 'auto', minWidth: 200 }}>
            <MenuList dense>
              {mentionSuggestions.users && mentionSuggestions.users.length > 0 && (
                <>
                  <MenuItem disabled sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}>
                    Utilisateurs
                  </MenuItem>
                  {mentionSuggestions.users.map(user => (
                    <MenuItem
                      key={user.id}
                      onClick={() => insertMention(user, 'user')}
                      sx={{ fontSize: '0.85rem' }}
                    >
                      <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.7rem' }}>
                        {user.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          {user.name}
                        </Typography>
                        {user.functional_role && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {user.functional_role}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </>
              )}
              {mentionSuggestions.tasks && mentionSuggestions.tasks.length > 0 && (
                <>
                  {mentionSuggestions.users && mentionSuggestions.users.length > 0 && <Divider />}
                  <MenuItem disabled sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}>
                    Tâches
                  </MenuItem>
                  {mentionSuggestions.tasks.map(task => (
                    <MenuItem
                      key={task.id}
                      onClick={() => insertMention(task, 'task')}
                      sx={{ fontSize: '0.85rem' }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          #{task.id} - {task.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
                          <Chip label={task.status} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
                          {task.priority && (
                            <Chip label={task.priority} size="small" color="warning" sx={{ height: 16, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </>
              )}
              {(!mentionSuggestions.users || mentionSuggestions.users.length === 0) &&
               (!mentionSuggestions.tasks || mentionSuggestions.tasks.length === 0) && (
                <MenuItem disabled sx={{ fontSize: '0.8rem' }}>
                  Aucune suggestion
                </MenuItem>
              )}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Message Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => { setReplyingTo(selectedMessage); handleCloseMenu(); }}>
          <ReplyIcon fontSize="small" sx={{ mr: 1 }} /> Répondre
        </MenuItem>
        <MenuItem onClick={handlePinMessage}>
          <PinIcon fontSize="small" sx={{ mr: 1 }} /> {selectedMessage?.is_pinned ? 'Détacher' : 'Épingler'}
        </MenuItem>
        <Divider />
        <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
          {REACTION_EMOJIS.map(emoji => (
            <IconButton key={emoji} size="small" onClick={() => handleReaction(emoji)}>
              {emoji}
            </IconButton>
          ))}
        </Box>
      </Menu>
    </Box>
  );
};

export default ProjectChat;